import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Bell, MessageSquare, Brain, Globe, Clock, CheckCircle } from 'lucide-react';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

const ChatSettings = () => {
  const { success, error: showError, info } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    responseStyle: 'balanced',
    aiProvider: 'gemini',
    autoSaveConversations: true,
    showFollowUpQuestions: true,
    enableNotifications: true,
    defaultCategory: 'all',
    responseLength: 'medium',
    includeSources: true,
    markdownFormatting: true,
    conversationHistory: true,
    smartSuggestions: true,
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await AIService.getChatSettings();
      if (response.success && response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching chat settings:', error);
      showError('Failed to load chat settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await AIService.updateChatSettings(settings);
      if (response.success) {
        success('Chat settings saved successfully');
      } else {
        showError('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving chat settings:', error);
      showError(error.message || 'Failed to save chat settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      responseStyle: 'balanced',
      aiProvider: 'gemini',
      autoSaveConversations: true,
      showFollowUpQuestions: true,
      enableNotifications: true,
      defaultCategory: 'all',
      responseLength: 'medium',
      includeSources: true,
      markdownFormatting: true,
      conversationHistory: true,
      smartSuggestions: true,
      language: 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    info('Settings reset to defaults');
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Chat Settings"
        description="Customize your AI assistant preferences and behavior."
        actions={[
          {
            label: isSaving ? 'Saving...' : 'Save Settings',
            icon: <Save size={18} />,
            onClick: handleSave,
            variant: 'primary',
            disabled: isSaving
          },
          {
            label: 'Reset to Defaults',
            icon: <RefreshCw size={18} />,
            onClick: handleReset,
            variant: 'outline'
          }
        ]}
      />
      
      <div className="page-container">
        {isLoading ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '60px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid var(--primary-color)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#6b7280',
              margin: 0
            }}>
              Loading settings...
            </h4>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '24px', marginTop: '24px' }}>
            {/* Response Preferences */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 20px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Brain size={20} style={{ color: 'var(--primary-color)' }} />
                Response Preferences
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Response Style
                  </label>
                  <select
                    value={settings.responseStyle}
                    onChange={(e) => updateSetting('responseStyle', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="concise">Concise - Brief and to the point</option>
                    <option value="balanced">Balanced - Moderate detail</option>
                    <option value="detailed">Detailed - Comprehensive explanations</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Response Length
                  </label>
                  <select
                    value={settings.responseLength}
                    onChange={(e) => updateSetting('responseLength', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="short">Short (100-200 words)</option>
                    <option value="medium">Medium (200-500 words)</option>
                    <option value="long">Long (500+ words)</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    AI Provider
                  </label>
                  <select
                    value={settings.aiProvider}
                    onChange={(e) => updateSetting('aiProvider', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="gemini">Google Gemini</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Conversation Settings */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 20px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <MessageSquare size={20} style={{ color: 'var(--primary-color)' }} />
                Conversation Settings
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Auto-save Conversations
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Automatically save all conversations to chat history
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.autoSaveConversations}
                    onChange={(e) => updateSetting('autoSaveConversations', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Show Follow-up Questions
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Display AI-generated follow-up questions after responses
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showFollowUpQuestions}
                    onChange={(e) => updateSetting('showFollowUpQuestions', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Enable Conversation History
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Maintain conversation context across sessions
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.conversationHistory}
                    onChange={(e) => updateSetting('conversationHistory', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Include Data Sources
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Show data sources and references in AI responses
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.includeSources}
                    onChange={(e) => updateSetting('includeSources', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Markdown Formatting
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Format AI responses with markdown for better readability
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.markdownFormatting}
                    onChange={(e) => updateSetting('markdownFormatting', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Notification Settings */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 20px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Bell size={20} style={{ color: 'var(--primary-color)' }} />
                Notification Settings
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Enable Notifications
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Receive notifications for AI responses and suggestions
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enableNotifications}
                    onChange={(e) => updateSetting('enableNotifications', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      Smart Suggestions
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Enable AI-powered smart suggestions for contracts and assets
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.smartSuggestions}
                    onChange={(e) => updateSetting('smartSuggestions', e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </label>
              </div>
            </div>

            {/* General Settings */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 20px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Globe size={20} style={{ color: 'var(--primary-color)' }} />
                General Settings
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Default Category
                  </label>
                  <select
                    value={settings.defaultCategory}
                    onChange={(e) => updateSetting('defaultCategory', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="all">All Categories</option>
                    <option value="contracts">Contracts</option>
                    <option value="assets">Assets</option>
                    <option value="compliance">Compliance</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="risk">Risk</option>
                    <option value="vendors">Vendors</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Language
                  </label>
                  <select
                    value={settings.language}
                    onChange={(e) => updateSetting('language', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Timezone
                  </label>
                  <input
                    type="text"
                    value={settings.timezone}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ChatSettings;
